import { useState, useEffect, useMemo, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { Modal } from '../common/Modal';
import { usePendingApprovals } from '../../hooks/usePendingApprovals';
import { useAlgoliaSearch } from '../../hooks/useAlgoliaSearch';
import { useAuth } from '../../hooks/useAuth';
import {
  getImageUrl,
  getWatchBrands,
  checkBrandIdExists,
  computeContentKey,
  getSubmissionIndexEntry,
  retrySubmissionProcessing,
  type SubmissionIndexEntry,
} from '../../services/firebase';
import type {
  PendingReferenceApproval,
  WatchBrand,
  WatchReferenceAlgoliaHit,
} from '../../types';
import { normalizeText } from '../../utils/text';
import { deriveBrandSlug } from '../../services/brandSlug';
import {
  formatMultiLookupValues,
  multiLookupValuesForWrite,
  parseMultiLookupValues,
} from '../../utils/multiLookupValues';
import toast from 'react-hot-toast';

// Helper to safely convert Firestore timestamp to Date
function toDate(timestamp: unknown): Date | null {
  if (!timestamp) return null;
  if (typeof timestamp === 'object' && 'toDate' in timestamp && typeof (timestamp as { toDate: unknown }).toDate === 'function') {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === 'object' && 'seconds' in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return null;
}

function formatTimestamp(timestamp: unknown): string {
  const date = toDate(timestamp);
  if (!date) return '-';
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return '-';
  }
}

// Helper to recursively remove undefined values from objects (Firestore rejects undefined)
function removeUndefined<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined) as T;
  }
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (value !== undefined) {
        result[key] = removeUndefined(value);
      }
    }
    return result as T;
  }
  return obj;
}

// Normalize string for fuzzy matching (strip diacritics + non-alphanumeric)
/**
 * Reference equality for the exact-match pin.
 *
 * `normalizeText` only folds accents and case, which is not enough here: a
 * submitter types "116610 LN" for a catalogue that stores "116610LN", and the
 * whole point of the pin is to recognise that pair. Punctuation and spacing
 * carry no meaning in a reference number, so they come out.
 */
function referenceKey(value?: string): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function normalize(str: string): string {
  return normalizeText(str).replace(/[^a-z0-9]/g, '');
}

// Fuzzy match score (higher = better match)
function fuzzyMatchScore(query: string, target: string): number {
  const normQuery = normalize(query);
  const normTarget = normalize(target);

  if (normQuery === normTarget) return 100; // Exact match
  if (normTarget.includes(normQuery)) return 80; // Contains
  if (normQuery.includes(normTarget)) return 60; // Query contains target

  // Check if all characters of query appear in order in target
  let queryIdx = 0;
  for (const char of normTarget) {
    if (char === normQuery[queryIdx]) {
      queryIdx++;
      if (queryIdx === normQuery.length) break;
    }
  }
  if (queryIdx === normQuery.length) return 40; // Subsequence match

  return 0;
}

// Find best matching brand
function findBestBrandMatch(
  brandName: string,
  brands: WatchBrand[]
): { brand: WatchBrand; score: number } | null {
  if (!brandName || brands.length === 0) return null;

  let bestMatch: { brand: WatchBrand; score: number } | null = null;

  for (const brand of brands) {
    // Check main name
    let score = fuzzyMatchScore(brandName, brand.name);

    // Check display name
    if (brand.displayName) {
      score = Math.max(score, fuzzyMatchScore(brandName, brand.displayName));
    }

    // Check alternative names
    if (brand.alternativeNames) {
      for (const altName of brand.alternativeNames) {
        score = Math.max(score, fuzzyMatchScore(brandName, altName));
      }
    }

    // Check abbreviations
    if (brand.abbreviations) {
      for (const abbr of brand.abbreviations) {
        score = Math.max(score, fuzzyMatchScore(brandName, abbr));
      }
    }

    if (score > 0 && (!bestMatch || score > bestMatch.score)) {
      bestMatch = { brand, score };
    }
  }

  return bestMatch;
}

// Generate brand ID from name
function generateBrandId(name: string): string {
  return deriveBrandSlug(name);
}

// Input field component - defined outside to prevent re-creation on every render
function InputField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
      />
    </div>
  );
}

interface PendingReferenceEditorProps {
  approval: PendingReferenceApproval;
  onClose: () => void;
  onSuccess: () => void;
}

export function PendingReferenceEditor({
  approval,
  onClose,
  onSuccess,
}: PendingReferenceEditorProps) {
  const { approve, reject, update, dismissDuplicate } = usePendingApprovals();
  const { user } = useAuth();

  // All watch brands for brand selection
  const [allBrands, setAllBrands] = useState<WatchBrand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(true);

  // Image loading
  const [loadedImageUrl, setLoadedImageUrl] = useState<string | null>(null);

  // Dedup index surfacing
  const [contentKey, setContentKey] = useState<string | null>(
    approval.submissionContentKey ?? null,
  );
  const [indexEntry, setIndexEntry] = useState<SubmissionIndexEntry | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const key =
          approval.submissionContentKey ||
          (await computeContentKey(
            approval.brandId || approval.brandName || '',
            approval.reference || '',
          ));
        if (cancelled) return;
        setContentKey(key);
        const entry = await getSubmissionIndexEntry(key);
        if (!cancelled) setIndexEntry(entry);
      } catch {
        // Non-fatal: index surfacing is a debugging aid only.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [approval.submissionContentKey, approval.brandId, approval.brandName, approval.reference]);

  // Brand selection state
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [newBrandId, setNewBrandId] = useState('');
  const [brandIdError, setBrandIdError] = useState<string | null>(null);
  const [validatingBrandId, setValidatingBrandId] = useState(false);
  const brandDropdownRef = useRef<HTMLDivElement>(null);

  // Form state - basic info
  const [brandName, setBrandName] = useState(approval.brandName || '');
  const [reference, setReference] = useState(approval.reference || '');
  const [model, setModel] = useState(approval.model || '');
  const [collection, setCollection] = useState(approval.collection || '');
  const [productName, setProductName] = useState(approval.productName || '');
  const [nickname, setNickname] = useState(approval.nickname || '');
  const [calibre, setCalibre] = useState(approval.calibre || '');
  const [movementType, setMovementType] = useState(approval.movementType || '');
  const [watchType, setWatchType] = useState(approval.watchType || '');
  const [specialEdition, setSpecialEdition] = useState(approval.specialEdition || false);
  const [series, setSeries] = useState(approval.series || '');
  const [bezel, setBezel] = useState(approval.bezel || '');
  const [color, setColor] = useState(approval.color || '');
  const [glass, setGlass] = useState(approval.glass || '');
  const [crystal, setCrystal] = useState(approval.crystal || '');

  // Case info
  const [caseDiameter, setCaseDiameter] = useState(approval.case?.diameter?.toString() || '');
  const [caseThickness, setCaseThickness] = useState(approval.case?.thickness?.toString() || '');
  const [caseLugToLug, setCaseLugToLug] = useState(approval.case?.lugToLug?.toString() || '');
  const [caseLugWidth, setCaseLugWidth] = useState(approval.case?.lugWidth?.toString() || '');
  const [caseMaterial, setCaseMaterial] = useState(approval.case?.material || '');
  const [caseWaterResistance, setCaseWaterResistance] = useState(approval.case?.waterResistance?.toString() || '');
  const [caseShape, setCaseShape] = useState(approval.case?.shape || '');
  const [caseCaseBack, setCaseCaseBack] = useState(approval.case?.caseBack || '');

  // Production info
  const [prodStartYear, setProdStartYear] = useState(approval.production?.startYear?.toString() || '');
  const [prodEndYear, setProdEndYear] = useState(approval.production?.endYear?.toString() || '');
  const [prodReleaseYear, setProdReleaseYear] = useState(approval.production?.releaseYear?.toString() || '');

  // Strap info
  const [strapMaterial, setStrapMaterial] = useState(approval.strap?.material || '');
  const [strapColor, setStrapColor] = useState(approval.strap?.color || '');
  const [strapWidth, setStrapWidth] = useState(approval.strap?.width?.toString() || '');
  const [strapBuckleType, setStrapBuckleType] = useState(approval.strap?.buckleType || '');

  // Dial and hands
  const [dialColor, setDialColor] = useState(approval.dialAndHands?.color || '');
  const [dialMaterial, setDialMaterial] = useState(approval.dialAndHands?.material || '');
  const [dialFinish, setDialFinish] = useState(approval.dialAndHands?.finish || '');
  // `numerals`/`indexes` store as string[] since v1.51.0 but still arrive as a
  // bare string from pre-migration docs and from AI/import-seeded approvals.
  // These two stay free-text fields here, so the array is flattened for display
  // and re-split on save; `formatMultiLookupValues` accepts either shape.
  const [dialNumerals, setDialNumerals] = useState(
    formatMultiLookupValues(approval.dialAndHands?.numerals),
  );
  const [dialHands, setDialHands] = useState(approval.dialAndHands?.hands || '');
  const [dialIndexes, setDialIndexes] = useState(
    formatMultiLookupValues(approval.dialAndHands?.indexes),
  );
  const [dialLuminova, setDialLuminova] = useState(approval.dialAndHands?.luminova || false);

  // Features
  const [features, setFeatures] = useState<string[]>(approval.features || []);
  const [newFeature, setNewFeature] = useState('');

  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    brand: true,
    basic: true,
    case: false,
    production: false,
    strap: false,
    dial: false,
    features: false,
  });

  // Load brands on mount
  useEffect(() => {
    getWatchBrands()
      .then((brands) => {
        setAllBrands(brands.sort((a, b) => a.name.localeCompare(b.name)));
        setBrandsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load brands:', err);
        setBrandsLoading(false);
      });
  }, []);

  // Close brand dropdown when clicking outside
  useEffect(() => {
    if (!showBrandDropdown) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(e.target as Node)) {
        setShowBrandDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBrandDropdown]);

  // Load image - check images array first (like WatchReference), then fallback to other fields
  useEffect(() => {
    const imagePath =
      approval.images?.[0]?.storagePath ||
      approval.pendingImagePath ||
      approval.imageUrls?.[0];
    if (imagePath) {
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        setLoadedImageUrl(imagePath);
      } else {
        getImageUrl(imagePath)
          .then((url) => setLoadedImageUrl(url))
          .catch(() => setLoadedImageUrl(null));
      }
    } else {
      setLoadedImageUrl(null);
    }
  }, [approval.images, approval.pendingImagePath, approval.imageUrls]);

  // Find suggested brand match
  const suggestedMatch = useMemo(() => {
    if (!brandName || allBrands.length === 0) return null;
    return findBestBrandMatch(brandName, allBrands);
  }, [brandName, allBrands]);

  // Filter brands for dropdown
  const filteredBrands = useMemo(() => {
    if (!brandSearchTerm) return allBrands.slice(0, 20);
    const term = normalizeText(brandSearchTerm);
    return allBrands
      .filter((b) =>
        normalizeText(b.name).includes(term) ||
        normalizeText(b.displayName ?? '').includes(term) ||
        b.alternativeNames?.some((n) => normalizeText(n).includes(term)) ||
        b.abbreviations?.some((a) => normalizeText(a).includes(term))
      )
      .slice(0, 20);
  }, [allBrands, brandSearchTerm]);

  // Get selected brand object
  const selectedBrand = useMemo(() => {
    if (!selectedBrandId) return null;
    return allBrands.find((b) => b.id === selectedBrandId) || null;
  }, [selectedBrandId, allBrands]);

  // Determine if we're creating a new brand
  const isCreatingNewBrand = !selectedBrandId && brandName.trim().length > 0;

  // Auto-generate brand ID when creating new brand
  useEffect(() => {
    if (isCreatingNewBrand && !newBrandId) {
      setNewBrandId(generateBrandId(brandName));
    }
  }, [isCreatingNewBrand, brandName, newBrandId]);

  // Validate brand ID uniqueness
  useEffect(() => {
    if (!isCreatingNewBrand || !newBrandId) {
      setBrandIdError(null);
      return;
    }

    // Check if ID matches an existing brand
    const existingBrand = allBrands.find((b) => b.id === newBrandId);
    if (existingBrand) {
      setBrandIdError(`ID "${newBrandId}" already exists (${existingBrand.name})`);
      return;
    }

    // Debounced check against Firestore
    const timeoutId = setTimeout(async () => {
      setValidatingBrandId(true);
      try {
        const exists = await checkBrandIdExists(newBrandId);
        if (exists) {
          setBrandIdError(`ID "${newBrandId}" already exists`);
        } else {
          setBrandIdError(null);
        }
      } catch {
        // Ignore errors, assume valid
        setBrandIdError(null);
      } finally {
        setValidatingBrandId(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [isCreatingNewBrand, newBrandId, allBrands]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleUseSuggestedBrand = () => {
    if (suggestedMatch) {
      setSelectedBrandId(suggestedMatch.brand.id);
      setBrandName(suggestedMatch.brand.name);
      setNewBrandId('');
      setBrandIdError(null);
    }
  };

  const handleSelectBrand = (brand: WatchBrand) => {
    setSelectedBrandId(brand.id);
    setBrandName(brand.name);
    setShowBrandDropdown(false);
    setBrandSearchTerm('');
    setNewBrandId('');
    setBrandIdError(null);
  };

  const handleClearBrand = () => {
    setSelectedBrandId(null);
    setNewBrandId(generateBrandId(brandName));
  };

  const handleAddFeature = () => {
    if (newFeature.trim() && !features.includes(newFeature.trim())) {
      setFeatures([...features, newFeature.trim()]);
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (feature: string) => {
    setFeatures(features.filter((f) => f !== feature));
  };

  // Build the update data object
  const buildUpdateData = () => {
    const data: Partial<PendingReferenceApproval> = {
      brandName,
      reference,
      model,
      collection,
      productName: productName || undefined,
      nickname: nickname || undefined,
      calibre: calibre || undefined,
      movementType: movementType || undefined,
      watchType: watchType || undefined,
      specialEdition,
      series: series || undefined,
      bezel: bezel || undefined,
      color: color || undefined,
      glass: glass || undefined,
      crystal: crystal || undefined,
    };

    // Case
    if (caseDiameter || caseThickness || caseLugToLug || caseLugWidth || caseMaterial || caseWaterResistance || caseShape || caseCaseBack) {
      data.case = {
        diameter: caseDiameter ? parseFloat(caseDiameter) : undefined,
        thickness: caseThickness ? parseFloat(caseThickness) : undefined,
        lugToLug: caseLugToLug ? parseFloat(caseLugToLug) : undefined,
        lugWidth: caseLugWidth ? parseFloat(caseLugWidth) : undefined,
        material: caseMaterial || undefined,
        waterResistance: caseWaterResistance || undefined,
        shape: caseShape || undefined,
        caseBack: caseCaseBack || undefined,
      };
    }

    // Production
    if (prodStartYear || prodEndYear || prodReleaseYear) {
      data.production = {
        startYear: prodStartYear ? parseInt(prodStartYear) : undefined,
        endYear: prodEndYear ? parseInt(prodEndYear) : undefined,
        releaseYear: prodReleaseYear ? parseInt(prodReleaseYear) : undefined,
      };
    }

    // Strap
    if (strapMaterial || strapColor || strapWidth || strapBuckleType) {
      data.strap = {
        material: strapMaterial || undefined,
        color: strapColor || undefined,
        width: strapWidth ? parseFloat(strapWidth) : undefined,
        buckleType: strapBuckleType || undefined,
      };
    }

    // Dial and hands
    if (dialColor || dialMaterial || dialFinish || dialNumerals || dialHands || dialIndexes || dialLuminova) {
      data.dialAndHands = {
        color: dialColor || undefined,
        material: dialMaterial || undefined,
        finish: dialFinish || undefined,
        numerals: multiLookupValuesForWrite(parseMultiLookupValues(dialNumerals)),
        hands: dialHands || undefined,
        indexes: multiLookupValuesForWrite(parseMultiLookupValues(dialIndexes)),
        luminova: dialLuminova,
        // Dual-name trap shim — the RN app reads only `lume`, so an approval
        // saved with just `luminova` renders with no "Luminous" row.
        lume: dialLuminova,
      };
    }

    // Features
    if (features.length > 0) {
      data.features = features;
    }

    return removeUndefined(data);
  };

  const handleSave = async () => {
    setIsProcessing(true);
    try {
      await update(approval.id, buildUpdateData());
      toast.success('Changes saved');
    } catch {
      toast.error('Failed to save changes');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetry = async () => {
    setIsProcessing(true);
    try {
      await retrySubmissionProcessing(approval.id);
      toast.success('Re-ran processing — refreshing');
      onSuccess();
    } catch (err) {
      console.error('Retry failed:', err);
      toast.error('Retry failed — see console');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Catalogue lookup — "is this reference already curated?"
   *
   * The reviewer used to leave this screen to answer that. The search is
   * brand-scoped, so it only makes sense once the brand is known to be an
   * existing one: in new-brand mode there is no brand to search under, and a
   * brand-less search would return the whole catalogue.
   */
  const confirmedBrandId = useMemo(() => {
    if (selectedBrandId) return selectedBrandId;
    // A submission that already carries a brandId resolving to a real brand is
    // just as confirmed as one the reviewer picked by hand; making them pick it
    // again would be friction with no decision behind it.
    if (approval.brandId && allBrands.some((b) => b.id === approval.brandId)) {
      return approval.brandId;
    }
    return null;
  }, [selectedBrandId, approval.brandId, allBrands]);

  const {
    results: catalogueHits,
    loading: catalogueLoading,
    error: catalogueError,
    search: searchCatalogue,
  } = useAlgoliaSearch();

  const [catalogueTerm, setCatalogueTerm] = useState(approval.reference || '');
  const [selectedMatch, setSelectedMatch] = useState<WatchReferenceAlgoliaHit | null>(null);
  const [notInDatabase, setNotInDatabase] = useState(false);
  const [confirmDismiss, setConfirmDismiss] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const autoSearchedFor = useRef<string | null>(null);

  // Fire once per (brand, reference) with the submitted number already in the
  // box — the reviewer's first question is always "this exact one?", and making
  // them press Search to ask it would be a step for nothing.
  useEffect(() => {
    if (!confirmedBrandId || !approval.reference) return;
    const key = `${confirmedBrandId}:${approval.reference}`;
    if (autoSearchedFor.current === key) return;
    autoSearchedFor.current = key;
    setCatalogueTerm(approval.reference);
    void searchCatalogue(approval.reference, { brandId: confirmedBrandId, limit: 8 });
  }, [confirmedBrandId, approval.reference, searchCatalogue]);

  /**
   * Every hit, capped, with an exact reference match pinned first.
   *
   * Not exact-match-only: a submitted reference routinely differs from the
   * curated one by a suffix or a space, and that near-miss is exactly the case
   * a human is here to judge. Showing only exact matches would answer "no
   * match" on those and send the reviewer back to the lookup this replaces.
   */
  const orderedHits = useMemo(() => {
    const target = referenceKey(approval.referenceNormalized || approval.reference);
    const hits = catalogueHits.slice(0, 8);
    if (!target) return hits;
    return [...hits].sort((a, b) => {
      const aExact = referenceKey(a.referenceNormalized || a.reference) === target ? 0 : 1;
      const bExact = referenceKey(b.referenceNormalized || b.reference) === target ? 0 : 1;
      return aExact - bExact;
    });
  }, [catalogueHits, approval.reference, approval.referenceNormalized]);

  const isExactMatch = (hit: WatchReferenceAlgoliaHit) => {
    const target = referenceKey(approval.referenceNormalized || approval.reference);
    return !!target && referenceKey(hit.referenceNormalized || hit.reference) === target;
  };

  const runCatalogueSearch = () => {
    if (!confirmedBrandId || !catalogueTerm.trim()) return;
    void searchCatalogue(catalogueTerm.trim(), { brandId: confirmedBrandId, limit: 8 });
  };

  /**
   * "Already in database" — close the submission against an existing reference.
   *
   * The service locks the dedup ledger first and only writes the status if that
   * succeeded, so a failure here means NOTHING changed and the reviewer can
   * simply try again. The message says so rather than leaving them wondering
   * which half went through.
   */
  const handleDismissDuplicate = async () => {
    if (!selectedMatch) return;
    setDismissing(true);
    try {
      await dismissDuplicate(
        approval.id,
        { brandId: selectedMatch.brandId, referenceId: selectedMatch.referenceId },
        user?.uid,
      );
      toast.success(
        `Dismissed — already in the catalogue as ${selectedMatch.reference || selectedMatch.referenceId}.`,
      );
      onSuccess();
    } catch (err) {
      console.error('Failed to dismiss as duplicate:', err);
      toast.error('Could not dismiss. Nothing was changed — try again.');
    } finally {
      setDismissing(false);
      setConfirmDismiss(false);
    }
  };

  const handleApprove = async () => {
    // Validate brand selection
    if (isCreatingNewBrand) {
      if (!newBrandId.trim()) {
        toast.error('Please enter a brand ID for the new brand');
        return;
      }
      if (brandIdError) {
        toast.error('Please fix the brand ID error before approving');
        return;
      }
    }

    setIsProcessing(true);
    try {
      // First save any changes
      const updateData = buildUpdateData();

      // If using existing brand, update brandId
      if (selectedBrandId) {
        updateData.brandId = selectedBrandId;
        if (selectedBrand) {
          updateData.brandName = selectedBrand.name;
        }
      } else if (isCreatingNewBrand) {
        // Set the new brand ID - the approval function will create the brand
        updateData.brandId = newBrandId;
      } else if (suggestedMatch && suggestedMatch.score >= 80) {
        // Auto-use suggested brand if high confidence match
        updateData.brandId = suggestedMatch.brand.id;
        updateData.brandName = suggestedMatch.brand.name;
      }

      await update(approval.id, updateData);
      await approve(approval.id);
      toast.success('Reference approved');
      onSuccess();
    } catch (err) {
      console.error('Error approving:', err);
      toast.error('Failed to approve reference');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    setIsProcessing(true);
    try {
      await reject(approval.id, rejectReason || undefined);
      toast.success('Reference rejected');
      onSuccess();
    } catch {
      toast.error('Failed to reject reference');
    } finally {
      setIsProcessing(false);
    }
  };

  const SectionHeader = ({ title, section }: { title: string; section: string }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="flex items-center justify-between w-full py-2 text-left font-medium text-gray-900 hover:text-brand-600"
    >
      {title}
      <svg
        className={`w-5 h-5 transform transition-transform ${expandedSections[section] ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );

  return (
    <>
      <Modal
        isOpen={!showRejectDialog}
        onClose={onClose}
        title={`Edit: ${approval.brandName} ${approval.reference || approval.model}`}
        size="xl"
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Status & Image Row */}
          <div className="flex gap-4">
            {/* Image */}
            <div className="w-32 h-32 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
              {loadedImageUrl ? (
                <img src={loadedImageUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                  No image
                </div>
              )}
            </div>

            {/* Status Info */}
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <Badge
                  variant={
                    approval.status === 'pending'
                      ? 'warning'
                      : approval.status === 'approved'
                      ? 'success'
                      : 'danger'
                  }
                >
                  {approval.status}
                </Badge>
                <span className="text-sm text-gray-500">
                  Submitted {formatTimestamp(approval.submittedAt)}
                </span>
              </div>
              {approval.processingError && (
                <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center justify-between gap-3">
                  <span>Error: {approval.processingError}</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleRetry}
                    loading={isProcessing}
                    title="Re-run catalogue processing for this approved submission"
                  >
                    Retry processing
                  </Button>
                </div>
              )}
              {indexEntry &&
                (indexEntry.status === 'approved' ||
                  indexEntry.status === 'rejected') && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    Duplicate: this reference is already{' '}
                    <strong>{indexEntry.status}</strong> in the submissions index
                    and is terminally locked. Approving again may create a
                    duplicate.
                  </div>
                )}
              {contentKey && (
                <div className="text-xs text-gray-400 font-mono">
                  contentKey: {contentKey}
                  {indexEntry ? ` · index: ${indexEntry.status}` : ' · index: —'}
                </div>
              )}
            </div>
          </div>

          {/* Brand Section */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <SectionHeader title="Brand" section="brand" />
            {expandedSections.brand && (
              <div className="mt-3 space-y-3">
                {/* Current selection display */}
                {selectedBrand ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div>
                      <span className="text-sm text-green-700">Using existing brand:</span>
                      <p className="font-medium text-green-900">{selectedBrand.name}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleClearBrand}>
                      Change
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Brand name input */}
                    <InputField
                      label="Brand Name"
                      value={brandName}
                      onChange={(v) => {
                        setBrandName(v);
                        setSelectedBrandId(null);
                        setNewBrandId(generateBrandId(v));
                      }}
                      placeholder="Enter brand name"
                    />

                    {/* Suggested match */}
                    {suggestedMatch && suggestedMatch.score >= 40 && (
                      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div>
                          <span className="text-sm text-blue-700">
                            Suggested match ({suggestedMatch.score >= 80 ? 'high' : 'partial'} confidence):
                          </span>
                          <p className="font-medium text-blue-900">{suggestedMatch.brand.name}</p>
                        </div>
                        <Button variant="secondary" size="sm" onClick={handleUseSuggestedBrand}>
                          Use This Brand
                        </Button>
                      </div>
                    )}

                    {/* Brand search/dropdown */}
                    <div className="relative" ref={brandDropdownRef}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Or select existing brand
                      </label>
                      <input
                        type="text"
                        value={brandSearchTerm}
                        onChange={(e) => {
                          setBrandSearchTerm(e.target.value);
                          setShowBrandDropdown(true);
                        }}
                        onFocus={() => setShowBrandDropdown(true)}
                        placeholder="Search brands..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                      {showBrandDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {brandsLoading ? (
                            <div className="px-3 py-2 text-sm text-gray-500">Loading brands...</div>
                          ) : filteredBrands.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">No brands found</div>
                          ) : (
                            filteredBrands.map((brand) => (
                              <button
                                key={brand.id}
                                type="button"
                                onClick={() => handleSelectBrand(brand)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex justify-between items-center"
                              >
                                <span>{brand.name}</span>
                                {brand.displayName && brand.displayName !== brand.name && (
                                  <span className="text-gray-400 text-xs">{brand.displayName}</span>
                                )}
                              </button>
                            ))
                          )}
                          <button
                            type="button"
                            onClick={() => setShowBrandDropdown(false)}
                            className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-100 border-t"
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </div>

                    {/* New brand warning */}
                    {isCreatingNewBrand && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800 font-medium mb-2">
                          A new brand will be created
                        </p>
                        <InputField
                          label="Brand ID (must be unique)"
                          value={newBrandId}
                          onChange={setNewBrandId}
                          placeholder="brand-id"
                        />
                        {validatingBrandId && (
                          <p className="text-xs text-gray-500 mt-1">Checking availability...</p>
                        )}
                        {brandIdError && (
                          <p className="text-xs text-red-600 mt-1">{brandIdError}</p>
                        )}
                        {!brandIdError && newBrandId && !validatingBrandId && (
                          <p className="text-xs text-green-600 mt-1">ID is available</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Catalogue check — is this reference already curated? */}
          {approval.status === 'pending' && confirmedBrandId && (
            <div className="border rounded-lg p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h4 className="text-sm font-medium text-gray-900">
                  Check the catalogue
                </h4>
                <span className="text-xs text-gray-500">
                  Searching {approval.brandName || confirmedBrandId} only
                </span>
              </div>

              {notInDatabase ? (
                <div className="mt-3 flex flex-wrap items-center gap-3 rounded bg-green-50 border border-green-200 px-3 py-2">
                  <span className="text-sm text-green-800">
                    Marked as not yet in the database — carry on with Approve below.
                  </span>
                  <button
                    type="button"
                    className="text-xs text-indigo-600 hover:underline"
                    onClick={() => setNotInDatabase(false)}
                  >
                    Search again
                  </button>
                </div>
              ) : (
                <>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      value={catalogueTerm}
                      aria-label="Search the curated catalogue"
                      onChange={(e) => setCatalogueTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          runCatalogueSearch();
                        }
                      }}
                      placeholder="Reference number"
                      className="flex-1 min-w-[12rem] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={runCatalogueSearch}
                      disabled={!catalogueTerm.trim() || catalogueLoading}
                    >
                      Search
                    </Button>
                  </div>

                  {catalogueError && (
                    <p className="mt-2 text-xs text-red-600">
                      Catalogue search unavailable: {catalogueError}
                    </p>
                  )}

                  {catalogueLoading ? (
                    <p className="mt-3 text-sm text-gray-500">Searching…</p>
                  ) : orderedHits.length === 0 ? (
                    <p className="mt-3 text-sm text-gray-500">
                      No curated references matched. That is not proof it is
                      absent — try the model name before deciding.
                    </p>
                  ) : (
                    <ul className="mt-3 divide-y divide-gray-100 border border-gray-200 rounded-lg">
                      {orderedHits.map((hit) => {
                        const chosen = selectedMatch?.objectID === hit.objectID;
                        return (
                          <li
                            key={hit.objectID}
                            className={`flex items-center gap-3 px-3 py-2 ${
                              chosen ? 'bg-brand-50' : ''
                            }`}
                          >
                            {hit.imageUrl && (
                              <img
                                src={hit.imageUrl}
                                alt=""
                                className="h-10 w-10 rounded object-cover shrink-0"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-mono text-gray-900">
                                  {hit.reference || hit.referenceId}
                                </span>
                                {isExactMatch(hit) && (
                                  <Badge variant="success">Exact reference match</Badge>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {[hit.model, hit.collection].filter(Boolean).join(' · ') || '—'}
                              </div>
                            </div>
                            <Button
                              variant={chosen ? 'primary' : 'ghost'}
                              size="sm"
                              onClick={() => {
                                setSelectedMatch(chosen ? null : hit);
                                setConfirmDismiss(false);
                              }}
                            >
                              {chosen ? 'Selected' : 'This one'}
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {confirmDismiss ? (
                      <>
                        <span className="text-sm text-gray-700">
                          Close this submission against{' '}
                          <strong>{selectedMatch?.reference || selectedMatch?.referenceId}</strong>?
                        </span>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={handleDismissDuplicate}
                          loading={dismissing}
                        >
                          Confirm dismiss
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmDismiss(false)}
                          disabled={dismissing}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmDismiss(true)}
                          disabled={!selectedMatch || isProcessing}
                          title={
                            selectedMatch
                              ? 'Dismiss this submission — the reference is already curated'
                              : 'Choose the matching catalogue reference first'
                          }
                        >
                          Already in database
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedMatch(null);
                            setNotInDatabase(true);
                          }}
                          disabled={isProcessing}
                        >
                          Not yet in database
                        </Button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Basic Info Section */}
          <div className="border rounded-lg p-4">
            <SectionHeader title="Basic Info" section="basic" />
            {expandedSections.basic && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <InputField label="Reference" value={reference} onChange={setReference} />
                <InputField label="Model" value={model} onChange={setModel} />
                <InputField label="Collection" value={collection} onChange={setCollection} />
                <InputField label="Product Name" value={productName} onChange={setProductName} />
                <InputField label="Nickname" value={nickname} onChange={setNickname} />
                <InputField label="Series" value={series} onChange={setSeries} />
                <InputField label="Calibre" value={calibre} onChange={setCalibre} />
                <InputField label="Movement Type" value={movementType} onChange={setMovementType} />
                <InputField label="Watch Type" value={watchType} onChange={setWatchType} />
                <InputField label="Bezel" value={bezel} onChange={setBezel} />
                <InputField label="Color" value={color} onChange={setColor} />
                <InputField label="Glass" value={glass} onChange={setGlass} />
                <InputField label="Crystal" value={crystal} onChange={setCrystal} />
                <div className="col-span-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="specialEdition"
                    checked={specialEdition}
                    onChange={(e) => setSpecialEdition(e.target.checked)}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <label htmlFor="specialEdition" className="text-sm text-gray-700">
                    Special Edition
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Case Section */}
          <div className="border rounded-lg p-4">
            <SectionHeader title="Case" section="case" />
            {expandedSections.case && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <InputField label="Diameter (mm)" value={caseDiameter} onChange={setCaseDiameter} type="number" />
                <InputField label="Thickness (mm)" value={caseThickness} onChange={setCaseThickness} type="number" />
                <InputField label="Lug to Lug (mm)" value={caseLugToLug} onChange={setCaseLugToLug} type="number" />
                <InputField label="Lug Width (mm)" value={caseLugWidth} onChange={setCaseLugWidth} type="number" />
                <InputField label="Material" value={caseMaterial} onChange={setCaseMaterial} />
                <InputField label="Water Resistance" value={caseWaterResistance} onChange={setCaseWaterResistance} placeholder="e.g., 100m or 10 ATM" />
                <InputField label="Shape" value={caseShape} onChange={setCaseShape} />
                <InputField label="Case Back" value={caseCaseBack} onChange={setCaseCaseBack} />
              </div>
            )}
          </div>

          {/* Production Section */}
          <div className="border rounded-lg p-4">
            <SectionHeader title="Production" section="production" />
            {expandedSections.production && (
              <div className="mt-3 grid grid-cols-3 gap-3">
                <InputField label="Start Year" value={prodStartYear} onChange={setProdStartYear} type="number" />
                <InputField label="End Year" value={prodEndYear} onChange={setProdEndYear} type="number" />
                <InputField label="Release Year" value={prodReleaseYear} onChange={setProdReleaseYear} type="number" />
              </div>
            )}
          </div>

          {/* Strap Section */}
          <div className="border rounded-lg p-4">
            <SectionHeader title="Strap" section="strap" />
            {expandedSections.strap && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <InputField label="Material" value={strapMaterial} onChange={setStrapMaterial} />
                <InputField label="Color" value={strapColor} onChange={setStrapColor} />
                <InputField label="Width (mm)" value={strapWidth} onChange={setStrapWidth} type="number" />
                <InputField label="Buckle Type" value={strapBuckleType} onChange={setStrapBuckleType} />
              </div>
            )}
          </div>

          {/* Dial & Hands Section */}
          <div className="border rounded-lg p-4">
            <SectionHeader title="Dial & Hands" section="dial" />
            {expandedSections.dial && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <InputField label="Color" value={dialColor} onChange={setDialColor} />
                <InputField label="Material" value={dialMaterial} onChange={setDialMaterial} />
                <InputField label="Finish" value={dialFinish} onChange={setDialFinish} />
                <InputField label="Numerals" value={dialNumerals} onChange={setDialNumerals} />
                <InputField label="Hands" value={dialHands} onChange={setDialHands} />
                <InputField label="Indexes" value={dialIndexes} onChange={setDialIndexes} />
                <div className="col-span-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="luminova"
                    checked={dialLuminova}
                    onChange={(e) => setDialLuminova(e.target.checked)}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <label htmlFor="luminova" className="text-sm text-gray-700">
                    Luminova
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Features Section */}
          <div className="border rounded-lg p-4">
            <SectionHeader title="Features" section="features" />
            {expandedSections.features && (
              <div className="mt-3 space-y-3">
                {features.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {features.map((feature) => (
                      <span
                        key={feature}
                        className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                      >
                        {feature}
                        <button
                          type="button"
                          onClick={() => handleRemoveFeature(feature)}
                          className="ml-1 text-gray-400 hover:text-red-500"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                    placeholder="Add feature..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                  <Button variant="secondary" size="sm" onClick={handleAddFeature}>
                    Add
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 pt-4 border-t flex justify-between">
          <Button variant="secondary" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleSave} loading={isProcessing}>
              Save Changes
            </Button>
            {approval.status === 'pending' && (
              <>
                <Button
                  variant="danger"
                  onClick={() => setShowRejectDialog(true)}
                  disabled={isProcessing}
                >
                  Reject
                </Button>
                <Button
                  variant="success"
                  onClick={handleApprove}
                  loading={isProcessing}
                  disabled={isProcessing || !!brandIdError}
                >
                  Approve
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Reject Dialog */}
      <Modal
        isOpen={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        title="Reject Reference"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to reject this reference submission?
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason (optional)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              rows={3}
              placeholder="Enter reason for rejection..."
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReject} loading={isProcessing}>
              Reject
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
